import { Button } from "@/components/ui/button";
import { Camera, X, Image as ImageIcon } from "lucide-react";
import { useState, useRef } from "react";
import { toast } from "sonner";
import { compressImage, formatFileSize } from "@/lib/imageCompression";

export interface FieldPhoto {
  file: File;
  preview: string;
  fieldContext: string;
  timestamp: Date;
  originalSize?: number;
  compressedSize?: number;
}

interface FieldPhotoCaptureProps {
  fieldContext: string;
  fieldLabel: string;
  onPhotoCapture: (photo: FieldPhoto) => void;
  onPhotoRemove?: (photo: FieldPhoto) => void;
  photos?: FieldPhoto[];
  disabled?: boolean;
}

export function FieldPhotoCapture({
  fieldContext,
  fieldLabel,
  onPhotoCapture,
  onPhotoRemove,
  photos = [],
  disabled = false,
}: FieldPhotoCaptureProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isCapturing, setIsCapturing] = useState(false);

  const handleCameraClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsCapturing(true);

    try {
      const file = files[0];
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }

      // Validate file size (max 16MB before compression)
      if (file.size > 16 * 1024 * 1024) {
        toast.error('Image must be smaller than 16MB');
        return;
      }

      // Compress image
      toast.info('Compressing image...');
      const compressionResult = await compressImage(file);
      const compressedFile = compressionResult.file;

      // Create preview URL from compressed file
      const preview = URL.createObjectURL(compressedFile);

      const photo: FieldPhoto = {
        file: compressedFile,
        preview,
        fieldContext,
        timestamp: new Date(),
        originalSize: compressionResult.originalSize,
        compressedSize: compressionResult.compressedSize,
      };

      onPhotoCapture(photo);
      
      // Show compression stats
      if (compressionResult.compressionRatio > 5) {
        toast.success(
          `Photo captured for ${fieldLabel}. Compressed from ${formatFileSize(compressionResult.originalSize)} to ${formatFileSize(compressionResult.compressedSize)} (${compressionResult.compressionRatio.toFixed(0)}% smaller)`
        );
      } else {
        toast.success(
          `Photo captured for ${fieldLabel}. Size: ${formatFileSize(compressionResult.compressedSize)}`
        );
      }
    } catch (error) {
      console.error('Error capturing photo:', error);
      toast.error('Failed to capture photo');
    } finally {
      setIsCapturing(false);
      // Reset input to allow capturing the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const fieldPhotos = photos.filter(p => p.fieldContext === fieldContext);

  return (
    <div className="flex items-center gap-2">
      {/* Camera Button */}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleCameraClick}
        disabled={disabled || isCapturing}
        className="flex items-center gap-1"
      >
        <Camera className="h-4 w-4" />
        <span className="hidden sm:inline">Photo</span>
      </Button>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Photo Thumbnails */}
      {fieldPhotos.length > 0 && (
        <div className="flex items-center gap-2">
          {fieldPhotos.map((photo, index) => (
            <div
              key={index}
              className="relative group w-12 h-12 rounded border-2 border-primary overflow-hidden"
            >
              <img
                src={photo.preview}
                alt={`${fieldLabel} photo ${index + 1}`}
                className="w-full h-full object-cover"
              />
              {onPhotoRemove && (
                <button
                  type="button"
                  onClick={() => onPhotoRemove(photo)}
                  className="absolute top-0 right-0 bg-destructive text-destructive-foreground rounded-bl p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  disabled={disabled}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
          <span className="text-xs text-muted-foreground">
            {fieldPhotos.length} {fieldPhotos.length === 1 ? 'photo' : 'photos'}
          </span>
        </div>
      )}
    </div>
  );
}
